import { useCallback, useRef, useState } from "react";
import { getDurableConversation } from "@/api/conversations";
import { controlTaskWorker } from "@/api/task-workers";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { openDurableConversationFrame } from "@/lib/open-durable-conversation-frame";
import { useAgentStore } from "@/stores/agent-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useTaskWorker } from "./use-task-worker";
import { translate } from "@/i18n";
import { hooksMessages } from "@/i18n/messages/hooks";

export type TaskWorkerAction = "open" | "open-parent" | "pause" | "resume";

/** Native Stop (pause) or Resume for the worker's current attempt. */
export interface TaskWorkerControl {
  action: "pause" | "resume";
  /** The provider has not yet confirmed this action. */
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
}

function actionErrorMessage(error: unknown): string {
  const detail = (error as { data?: { error?: unknown } } | null)?.data?.error;
  if (typeof detail === "string") return detail;
  return error instanceof Error
    ? error.message
    : translate(hooksMessages, "taskConversationUpdateFailed");
}

/** Task worker snapshot plus the open / open-parent / pause / resume actions
 *  shared by the worker composer and the task Details header. Errors go to
 *  `onError` when given, otherwise to `actionError`. */
export function useTaskWorkerActions(
  projectId: string | undefined,
  taskId: string | undefined,
  onError?: (message: string) => void,
) {
  const state = useTaskWorker(projectId, taskId);
  const { worker, error, acceptState, refresh } = state;
  // Keyed like the worker snapshot, so a host that switches tasks without
  // remounting never shows the previous task's error or busy state.
  const scopeKey = `${projectId}:${taskId}`;
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const inFlightRef = useRef(new Set<string>());
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [actionError, setActionError] = useState({ key: "", message: "" });

  const setScopeBusy = useCallback((key: string, busy: boolean) => {
    setBusyKeys((current) => {
      const next = new Set(current);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const run = useCallback(
    async (action: TaskWorkerAction) => {
      const opensConversation = action === "open" || action === "open-parent";
      if (!worker || !projectId || !taskId) return;
      if (inFlightRef.current.has(scopeKey)) return;
      if (!opensConversation && worker.pending_control_action) return;
      inFlightRef.current.add(scopeKey);
      setScopeBusy(scopeKey, true);
      setActionError({ key: scopeKey, message: "" });
      const isCurrentScope = captureDesktopChatAuthScopeGuard();
      try {
        if (opensConversation) {
          const conversationId =
            action === "open-parent"
              ? worker.parent_conversation_id
              : worker.conversation_id;
          if (!conversationId) return;
          const conversation = await getDurableConversation(conversationId);
          if (!isCurrentScope()) return;
          await openDurableConversationFrame({
            conversation,
            addToast: useNotificationStore.getState().addSystemToast,
            closePopover: () => {},
            selectAgent: useAgentStore.getState().selectAgent,
            shouldContinue: isCurrentScope,
          });
        } else if (worker.execution_id) {
          const updated = await controlTaskWorker(
            projectId,
            taskId,
            action,
            worker.execution_id,
          );
          // A reply for a task the host has switched away from must not
          // overwrite the current task's snapshot.
          if (isCurrentScope() && scopeKeyRef.current === scopeKey) {
            acceptState(updated);
            refresh();
          }
        }
      } catch (error) {
        if (!isCurrentScope()) return;
        const message = actionErrorMessage(error);
        if (onError) onError(message);
        else setActionError({ key: scopeKey, message });
      } finally {
        inFlightRef.current.delete(scopeKey);
        // Always clear: a host that survives a re-auth must not keep this
        // task's Stop disabled. A set after unmount is a no-op.
        setScopeBusy(scopeKey, false);
      }
    },
    [
      acceptState,
      onError,
      projectId,
      refresh,
      scopeKey,
      setScopeBusy,
      taskId,
      worker,
    ],
  );

  const busy = busyKeys.has(scopeKey);
  const pending = worker?.pending_control_action;
  const controlAction =
    !worker || error
      ? null
      : worker.can_pause || pending === "pause"
        ? "pause"
        : worker.can_resume || pending === "resume"
          ? "resume"
          : null;
  const control: TaskWorkerControl | null = controlAction
    ? {
        action: controlAction,
        pending: pending === controlAction,
        disabled: busy || !!pending,
        onClick: () => void run(controlAction),
      }
    : null;

  return {
    ...state,
    busy,
    actionError: actionError.key === scopeKey ? actionError.message : "",
    run,
    control,
  };
}
